type CaptureContext = {
   level: 'warning';
   tags: Record<string, string>;
   extra: Record<string, unknown>;
   fingerprint: string[];
};

type WatchOptions = {
   capture: (message: string, context: CaptureContext) => void;
};

// Some margin for rounding noise (scrollbars, injected extension widgets, etc)
const OVERFLOW_THRESHOLD_PX = 8;
// Re-measure before reporting to avoid reporting animations/resizes
const CONFIRM_DELAY_MS = 2000;
const RECHECK_DEBOUNCE_MS = 500;
const MAX_CULPRITS = 10;
// scrollWidth is an integer, getBoundingClientRect isn't, so leave subpixel slack
const EDGE_TOLERANCE_PX = 2;

const measureOverflow = () =>
   document.documentElement.scrollWidth - document.documentElement.clientWidth;

const describeCulprit = (el: Element, right: number, width: number): string => {
   const classes = (el.getAttribute('class') ?? '').trim().replace(/\s+/g, '.');
   return (
      `${el.tagName}${el.id ? `#${el.id}` : ''}${classes ? `.${classes}` : ''}` +
      ` right=${Math.round(right)} width=${Math.round(width)}`
   );
};

const isInsideSvg = (el: Element): boolean =>
   el instanceof SVGElement && el.ownerSVGElement !== null;

// Name the elements whose right edge lands on the document's overflow edge, so
// the Sentry event points at what pushed the page wide. Anchoring to that edge
// rather than to the viewport leaves out what sits past the viewport without
// widening the document: closed off-canvas panels, slides clipped inside a
// carousel, the icons riding along in them. Ties go to the narrowest match,
// which names the leaf ahead of the wrappers it stretched.
// O(n) over every element under `body`, but runs at most once per pageview and
// only after confirmed overflow, with the computed-style read limited to the
// elements past the viewport.
// Selectors only, no text content, so no user content reaches Sentry.
const findCulprits = (): string[] => {
   const doc = document.documentElement;
   const viewportWidth = doc.clientWidth;
   const overflowEdge = doc.scrollWidth;
   const scrollX = window.scrollX;
   const matches: { el: Element; right: number; width: number }[] = [];
   for (const el of Array.from(document.body.getElementsByTagName('*'))) {
      if (isInsideSvg(el)) {
         continue;
      }
      const rect = el.getBoundingClientRect();
      const right = rect.right + scrollX;
      if (right <= viewportWidth + 1 || right > overflowEdge + EDGE_TOLERANCE_PX) {
         continue;
      }
      if (getComputedStyle(el).position === 'fixed') {
         continue;
      }
      matches.push({ el, right, width: rect.width });
   }
   const atEdge = matches.filter(({ right }) => right >= overflowEdge - EDGE_TOLERANCE_PX);
   const ranked = atEdge.length
      ? atEdge.sort((a, b) => a.width - b.width)
      : matches.sort((a, b) => b.width - a.width);
   return ranked
      .slice(0, MAX_CULPRITS)
      .map(({ el, right, width }) => describeCulprit(el, right, width));
};

/** Report horizontal overflow to Sentry at most once per pageview. */
export function watchForHorizontalOverflow({ capture }: WatchOptions): void {
   let reported = false;
   let confirmTimer: ReturnType<typeof setTimeout> | undefined;
   let debounceTimer: ReturnType<typeof setTimeout> | undefined;
   let resizeObserver: ResizeObserver | undefined;

   const report = () => {
      reported = true;
      window.removeEventListener('resize', scheduleCheck);
      resizeObserver?.disconnect();
      capture('Page has unexpected horizontal overflow', {
         level: 'warning',
         tags: { overflowDiagnostic: 'true' },
         extra: {
            overflowPx: measureOverflow(),
            clientWidth: document.documentElement.clientWidth,
            scrollWidth: document.documentElement.scrollWidth,
            innerWidth: window.innerWidth,
            culprits: findCulprits(),
         },
         fingerprint: ['horizontal-overflow'],
      });
   };

   const check = () => {
      if (reported || confirmTimer || measureOverflow() <= OVERFLOW_THRESHOLD_PX) {
         return;
      }
      confirmTimer = setTimeout(() => {
         confirmTimer = undefined;
         if (!reported && measureOverflow() > OVERFLOW_THRESHOLD_PX) {
            report();
         }
      }, CONFIRM_DELAY_MS);
   };

   const scheduleCheck = () => {
      if (reported) {
         return;
      }
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(check, RECHECK_DEBOUNCE_MS);
   };

   const start = () => {
      check();
      window.addEventListener('resize', scheduleCheck);
      if (typeof ResizeObserver !== 'undefined') {
         resizeObserver = new ResizeObserver(scheduleCheck);
         resizeObserver.observe(document.body);
      }
   };

   const fontsSettled = () => (document.fonts ? document.fonts.ready : Promise.resolve());
   if (document.readyState === 'complete') {
      void fontsSettled().then(start);
   } else {
      window.addEventListener('load', () => void fontsSettled().then(start), { once: true });
   }
}
